import { useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { aktuelleSprache, beschriftung, t } from './sprache'
import { Nachfrage } from './Nachfrage'
import { useDarf } from './Rechte'
import type { Aufbauzeile } from './typen'
import { Zeilenmenue } from './Zeilenmenue'
import { Reihenfolgefenster } from './Reihenfolgefenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'

/** Ersatz, wenn die Vorschläge im Hintergrund nicht kamen (R1212): keine Vorschläge. */
const KEINE_LEGUNGEN: string[] = []

/**
 * Woraus eine Bewertungsebene besteht — der Baum über dem Blatt
 * (2.4 Abschnitt 9, R396–R400).
 *
 * **Dieselbe Bauweise wie die Positionsgliederung über dem Positionsplan**
 * (R317): Die Übersicht wählt aus, gepflegt wird mit dem Bauteil, das der
 * Anwender überall wiederfindet. Der Baum sagt zusätzlich etwas, das kein
 * Blatt sagen kann — **woraus HGB besteht**. Das hängt nicht an einer Zeile,
 * sondern an mehreren zugleich, und ein Eigenschaftsfeld dafür wäre entweder
 * eine Liste in einem Textfeld oder eine Beziehung, die nur eine einzige
 * Schicht zulässt.
 *
 * **Drei Arten, drei Bedeutungen** (R396). Die *Rohdaten* sind, was die
 * Gesellschaft geliefert hat — dorthin schreibt nur der Import, und das weist
 * die Datenbank durch, nicht die Maske (R397). Ein *Delta* ist eine
 * Anpassungsschicht mit eigenem Namen. Eine *Gruppe* ist deren Summe und
 * trägt selbst keine Zeile; deshalb steht an ihr auch nie eine Belegzahl.
 *
 * **Angelegt wird hier, nicht anderswo.** Ein Formular an anderer Stelle
 * müsste nach der Gruppe fragen — und die Antwort steht hier ohnehin da: Man
 * legt die Schicht unter der Gruppe an, unter der sie erscheinen soll.
 *
 * **Die Reihenfolge geht über zwei Pfeiltasten je Zeile** (R392), nicht über
 * das Ziehen: R143 lässt keine Funktion zu, die nur die Maus kennt. Sie
 * bestimmt die Spaltenfolge im Bericht — gerechnet wird additiv (R23), gelesen
 * wird in ihr.
 */
export function Ledgeraufbau({
  aufAuswahl,
  aufAenderung,
}: {
  /** Eine angeklickte Ebene im Blatt darunter aufschlagen (R317). */
  aufAuswahl?: (schluessel: string) => void
  aufAenderung?: () => void
}) {
  const [stand, setStand] = useState(0)
  // Der Aufbau ist der Inhalt der Seite — ein Hauptabruf (R1211).
  const aufbauabruf = useAbruf(() => api.ledgeraufbau(), [stand])
  const bild = aufbauabruf.wert
  /**
   * Welche Rechnungslegungen im Haus **schon in Gebrauch** sind (R702).
   *
   * **Die Auflage zum freien Textfeld.** Verglichen wird die Rechnungslegung
   * zeichengenau — die Stellschrauben einer Ausgabe hängen an ihr. Wer „hgb"
   * tippt, wo „HGB" steht, bekommt eine Ebene, zu der der Abschluss kein
   * Kapitel findet, und nichts bricht ab. Deshalb steht das Vorhandene unter
   * dem Feld und ist mit einem Griff zu übernehmen; getippt werden muss nur
   * das, was es noch nicht gibt.
   */
  // Nur Vorschläge, also im Hintergrund (7.2 Abschnitt 30, R1007; R1211).
  const legungen = useHintergrundabruf(
    () =>
      api.stammdatenliste('ledger').then((l) =>
        [
          ...new Set(
            l.eintraege
              // **Nur die Gruppen.** `lokal` gehört den Rohdaten — es ist der
              // Stand, den die Tochter geliefert hat, und keine
              // Rechnungslegung, unter der ein Konzernabschluss entsteht.
              // Als Vorschlag an einer neuen Gruppe wäre es eine falsche
              // Fährte.
              .filter((e) => e.werte.art === 'gruppe')
              .map((e) => e.werte.rechnungslegung ?? '')
              .filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b, 'de')),
      ),
    [stand],
    KEINE_LEGUNGEN,
  )
  /** Welche Schicht aus ihrer Gruppe genommen werden soll — die Frage steht offen (R394). */
  const [haengeAus, setHaengeAus] = useState<Aufbauzeile | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [anlegenAn, setAnlegenAn] = useState<string | null>(null)
  const [benenne, setBenenne] = useState<string | null>(null)
  // Rechtsklick → Reihenfolge ändern (7.4 Abschnitt 32, R1341).
  const [menue, setMenue] = useState<{ x: number; y: number; ziel: Reihenfolgeziel } | null>(null)
  const [reihenfolge, setReihenfolge] = useState<Reihenfolgeziel | null>(null)
  // Was der Angemeldete nicht darf, steht als Knopf gar nicht da (R1180).
  const darf = useDarf()

  async function tue(was: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)

    try {
      await was()
      setAnlegenAn(null)
      setBenenne(null)
      setStand((n) => n + 1)
      aufAenderung?.()
    } catch (fehler) {
      // Der Befund und sein Weg, nicht ein Fehlercode (R145): „HGB liegt
      // bereits in IFRS" sagt, was zu tun ist; „409" sagt es nicht. Eine
      // Störung meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  // Kam der Aufbau nicht, steht das am Ort, statt dass der Baum fehlt (R1210).
  if (aufbauabruf.ausgefallen) {
    return (
      <section className="gliederungsbaum ledgeraufbau">
        <div className="berichtsleiste">
          <h2>{t('ledgeraufbau.titel')}</h2>
        </div>
        <Abrufausfall abruf={aufbauabruf} />
      </section>
    )
  }

  if (!bild) {
    return null
  }

  const gruppen = bild.zeilen.filter((z) => z.art === 'gruppe')

  return (
    <section className="gliederungsbaum ledgeraufbau">
      <div className="berichtsleiste">
        <h2>{t('ledgeraufbau.titel')}</h2>

        {darf('schichtAnlegen') && (
          <button
            type="button"
            className="schmal"
            disabled={laeuft}
            onClick={() => setAnlegenAn(anlegenAn === '' ? null : '')}
          >
            {t('ledgeraufbau.neu.gruppe')}
          </button>
        )}
      </div>

      {meldung && <p className="hinweis">{meldung}</p>}

      {anlegenAn === '' && (
        <Anlageblatt
          titel={t('ledgeraufbau.neu.titel.wurzel')}
          ebene={0}
          arten={['gruppe']}
          laeuft={laeuft}
          legungen={legungen}
          aufAnlegen={(name, art, rechnungslegung) =>
            void tue(() => api.schichtAnlegen({ gruppe: null, name, art, rechnungslegung }))
          }
          aufAbbruch={() => setAnlegenAn(null)}
        />
      )}

      <ul className="baum">
        {bild.zeilen.map((z) => (
          <li key={`${z.gruppe ?? 'wurzel'}:${z.schluessel}`}>
            <Zeile
              zeile={z}
              laeuft={laeuft}
              benennt={benenne === `${z.gruppe ?? ''}:${z.schluessel}`}
              aufAuswahl={aufAuswahl}
              aufBenennen={
                darf('schichtBenennen')
                  ? (offen) => setBenenne(offen ? `${z.gruppe ?? ''}:${z.schluessel}` : null)
                  : undefined
              }
              aufName={(name) => void tue(() => api.schichtBenennen(z.schluessel, aktuelleSprache(), name))}
              aufAnlegen={
                z.art === 'gruppe' && darf('schichtAnlegen')
                  ? () => setAnlegenAn(anlegenAn === z.schluessel ? null : z.schluessel)
                  : undefined
              }
              // **Reihenfolge ändern** per Rechtsklick auf das Mitglied (7.4
              // Abschnitt 32, R1341) — bis zum 2026-10-06 zwei Pfeilknöpfe.
              aufMenue={
                z.gruppe && darf('reihenfolgeSetzen')
                  ? (x, y) =>
                      setMenue({
                        x,
                        y,
                        ziel: {
                          art: 'ledgerelement',
                          element: `${z.gruppe}|${z.schluessel}`,
                          titel: beschriftung(z.namen) || z.code,
                        },
                      })
                  : undefined
              }
              /*
                **Das Programm fragt selbst** (R394). `window.confirm` gab in
                dieser Hülle stillschweigend `false` zurück — der Knopf sah aus
                wie ein Knopf und tat nichts. Im Kachelbrett war das schon
                einmal behoben; **hier war es stehengeblieben**, bis die
                Funktionsprüfung es fand.
              */
              aufAushaengen={z.gruppe && darf('schichtAushaengen') ? () => setHaengeAus(z) : undefined}
            />

            {anlegenAn === z.schluessel && (
              <Anlageblatt
                titel={t('ledgeraufbau.neu.titel', { gruppe: beschriftung(z.namen) || z.code })}
                ebene={z.ebene + 1}
                arten={['delta', 'rohdaten', 'gruppe']}
                laeuft={laeuft}
                /* Was schon da ist, wird aufgenommen statt neu angelegt: Eine
                   zweite Rohdatenschicht neben derselben ersten wäre zweimal
                   dieselbe Lieferung (R399). */
                aufnehmbar={bild.zeilen
                  .concat(bild.freie)
                  .filter(
                    (k) =>
                      k.schluessel !== z.schluessel &&
                      !bild.zeilen.some(
                        (m) => m.gruppe === z.schluessel && m.schluessel === k.schluessel,
                      ),
                  )
                  .filter((k, i, alle) => alle.findIndex((a) => a.schluessel === k.schluessel) === i)}
                aufAnlegen={(name, art) =>
                  void tue(() => api.schichtAnlegen({ gruppe: z.schluessel, name, art }))
                }
                aufAufnehmen={
                  darf('schichtAufnehmen')
                    ? (mitglied) => void tue(() => api.schichtAufnehmen(z.schluessel, mitglied))
                    : undefined
                }
                aufAbbruch={() => setAnlegenAn(null)}
              />
            )}
          </li>
        ))}

        {gruppen.length === 0 && (
          <li>
            <p className="hinweis">{t('ledgeraufbau.leer')}</p>
          </li>
        )}
      </ul>

      {bild.freie.length > 0 && (
        <div className="freieschichten">
          <h3>{t('ledgeraufbau.freie')}</h3>
          <p className="erlaeuterung">{t('ledgeraufbau.freie.erlaeuterung')}</p>

          <ul className="baum">
            {bild.freie.map((z) => (
              <li key={z.schluessel}>
                <Zeile zeile={z} laeuft={laeuft} benennt={false} aufAuswahl={aufAuswahl} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        **Die Frage stellt das Programm selbst** (R394) — nicht der Browser.
        Die bestätigende Taste benennt die Tat, statt „Ja" zu sagen (R144).
      */}
      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={[{ schluessel: 'reihenfolge.menue', tun: () => { setReihenfolge(menue.ziel); setMenue(null) } }]}
          aufSchliessen={() => setMenue(null)}
        />
      )}

      {reihenfolge && (
        <Reihenfolgefenster
          ziel={reihenfolge}
          aufSchliessen={(geaendert) => {
            setReihenfolge(null)
            if (geaendert) void tue(async () => {})
          }}
        />
      )}

      {haengeAus && (
        <Nachfrage
          titel={t('ledgeraufbau.aushaengen')}
          frage={t('ledgeraufbau.aushaengen.frage', {
            mitglied: beschriftung(haengeAus.namen) || haengeAus.code,
            gruppe: haengeAus.gruppe ?? '',
          })}
          bestaetigen={t('ledgeraufbau.aushaengen')}
          aufSchliessen={() => setHaengeAus(null)}
          aufJa={() => {
            const zeile = haengeAus
            setHaengeAus(null)
            void tue(() => api.schichtAushaengen(zeile.gruppe ?? '', zeile.schluessel))
          }}
        />
      )}
    </section>
  )
}

/**
 * Eine Zeile des Baums.
 *
 * **Die Belegzahl steht an der Schicht und nicht an der Gruppe.** In eine
 * Gruppe wird nicht gebucht (R396); eine Null dort wäre keine Auskunft,
 * sondern eine Frage, die niemand gestellt hat.
 */
function Zeile({
  zeile,
  laeuft,
  benennt,
  aufAuswahl,
  aufBenennen,
  aufName,
  aufAnlegen,
  aufMenue,
  aufAushaengen,
}: {
  zeile: Aufbauzeile
  laeuft: boolean
  benennt: boolean
  aufAuswahl?: (schluessel: string) => void
  aufBenennen?: (offen: boolean) => void
  aufName?: (name: string) => void
  aufAnlegen?: () => void
  /** Rechtsklick auf die Zeile (7.4 Abschnitt 32, R1341). */
  aufMenue?: (x: number, y: number) => void
  aufAushaengen?: () => void
}) {
  const name = beschriftung(zeile.namen) || zeile.code

  return (
    <div
      className={`baumzeile ledgerzeile ${zeile.art}${
        zeile.rohdatenzahl !== null && zeile.rohdatenzahl !== 1 ? ' offen' : ''
      }`}
      style={{ paddingLeft: `${zeile.ebene * 22 + 8}px` }}
      onContextMenu={(e) => {
        if (!aufMenue) return
        e.preventDefault()
        aufMenue(e.clientX, e.clientY)
      }}
    >
      {/*
        **Der Schlüssel steht nicht vor dem Namen** (R699, Auftrag vom
        2026-09-22). Seit R676 ist `code` der Schlüssel — hier stand also
        *„LDG-HGB · HGB-Konzernabschluss"*, und die linke Hälfte sagte einem
        Menschen nichts, was die rechte nicht besser sagt. Wer den Schlüssel
        sucht, findet ihn am Blatt darunter als Objekt-ID und über dessen
        Suche, die ihn mitliest.
      */}
      {benennt && aufName ? (
        <span className="poscode">
          <Namensfeld
            name={name}
            laeuft={laeuft}
            aufName={aufName}
            aufAbbruch={() => aufBenennen?.(false)}
          />
        </span>
      ) : (
        <button type="button" className="poscode" onClick={() => aufAuswahl?.(zeile.schluessel)}>
          <span className="name">{name}</span>
        </button>
      )}

      <span className={`artmarke ${zeile.art}`} title={t(`ledgeraufbau.art.${zeile.art}.erlaeuterung`)}>
        {t(`ledgeraufbau.art.${zeile.art}`)}
      </span>

      {/* Dass noch nichts gebucht ist, ist eine Auskunft; wie viele Belege es
          sind, keine (7.2 Abschnitt 36, R1143). */}
      {zeile.art !== 'gruppe' && zeile.belegzahl === 0 && (
        <span className="belegzahl">{t('ledgeraufbau.belege.keine')}</span>
      )}

      {zeile.rohdatenzahl !== null && zeile.rohdatenzahl !== 1 && (
        <span className="befund">
          {t(zeile.rohdatenzahl === 0 ? 'ledgeraufbau.offen.keine' : 'ledgeraufbau.offen.mehrere')}
        </span>
      )}

      <span className="griffe">
        {aufBenennen && !benennt && (
          <button type="button" className="schmal" disabled={laeuft} onClick={() => aufBenennen(true)}>
            {t('ledgeraufbau.umbenennen')}
          </button>
        )}

        {aufAnlegen && (
          <button type="button" className="schmal" disabled={laeuft} onClick={aufAnlegen}>
            {t('ledgeraufbau.neu.schicht')}
          </button>
        )}

        {aufAushaengen && (
          <button type="button" className="schmal" disabled={laeuft} onClick={aufAushaengen}>
            {t('ledgeraufbau.aushaengen')}
          </button>
        )}
      </span>
    </div>
  )
}

/** Umbenennen an Ort und Stelle — der Name steht da, wo man ihn liest. */
function Namensfeld({
  name,
  laeuft,
  aufName,
  aufAbbruch,
}: {
  name: string
  laeuft: boolean
  aufName: (name: string) => void
  aufAbbruch: () => void
}) {
  const [wert, setWert] = useState(name)

  return (
    <input
      className="namensfeld"
      value={wert}
      autoFocus
      disabled={laeuft}
      onChange={(e) => setWert(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          aufName(wert)
        }
        if (e.key === 'Escape') {
          aufAbbruch()
        }
      }}
      onBlur={() => (wert.trim() && wert !== name ? aufName(wert) : aufAbbruch())}
    />
  )
}

/**
 * Anlegen und Einhängen in einem Schritt (R321, hier für die Schichten).
 *
 * Die Gruppe steht nicht im Formular — sie steht im Baum, eine Zeile darüber.
 *
 * **Gefragt wird nach der Bezeichnung, nicht nach einem Code** (R676, R699,
 * R702, Auftrag vom 2026-09-22). Bis dahin stand hier ein Codefeld, und es war
 * nicht folgenlos wie bei den Nachbarn: Aus ihm entstand der Schlüssel,
 * `LDG-` plus die Eingabe. Die Bewertungsebene war damit die letzte Stelle im
 * Haus, an der ein Anwender einen Schlüssel tippte.
 *
 * **An seine Stelle tritt die Rechnungslegung — und nur an der Wurzel**
 * (R702). Darunter erbt sie von der Gruppe: Eine Schicht unter „HGB" ist eine
 * HGB-Schicht, und danach ist niemand zu fragen. Ein Feld, dessen Antwort eine
 * Zeile höher steht, beantwortet keine Frage.
 */
function Anlageblatt({
  titel,
  ebene,
  arten,
  laeuft,
  legungen,
  aufnehmbar,
  aufAnlegen,
  aufAufnehmen,
  aufAbbruch,
}: {
  titel: string
  ebene: number
  arten: ('rohdaten' | 'delta' | 'gruppe')[]
  laeuft: boolean
  /** Die Rechnungslegungen, die es im Haus schon gibt — nur an der Wurzel (R702). */
  legungen?: string[]
  aufnehmbar?: Aufbauzeile[]
  aufAnlegen: (
    name: string,
    art: 'rohdaten' | 'delta' | 'gruppe',
    rechnungslegung?: string,
  ) => void
  aufAufnehmen?: (mitglied: string) => void
  aufAbbruch: () => void
}) {
  const [name, setName] = useState('')
  const [legung, setLegung] = useState('')
  // Ohne Vorgabe waere der Typ optional und die erste Auswahl leer -- ein
  // Formular, das mit einer Luecke beginnt, die es selbst kennt.
  const [art, setArt] = useState<'rohdaten' | 'delta' | 'gruppe'>(arten[0] ?? 'delta')

  /** An der Wurzel gibt es nichts zu erben — dort wird gefragt (R702). */
  const anDerWurzel = legungen !== undefined
  const vollstaendig = name.trim().length > 0 && (!anDerWurzel || legung.trim().length > 0)

  return (
    <div className="einfuegeblatt" style={{ marginLeft: `${ebene * 22 + 30}px` }}>
      <p className="erlaeuterung">{titel}</p>

      {arten.length > 1 && (
        <label className="suchzeile">
          <span className="name">{t('ledgeraufbau.neu.art')}</span>
          <select value={art} disabled={laeuft} onChange={(e) => setArt(e.target.value as typeof art)}>
            {arten.map((a) => (
              <option key={a} value={a}>
                {t(`ledgeraufbau.art.${a}`)}
              </option>
            ))}
          </select>
        </label>
      )}

      <p className="erlaeuterung">{t(`ledgeraufbau.art.${art}.erlaeuterung`)}</p>

      <label className="suchzeile">
        <span className="name">{t('ledgeraufbau.neu.name')}</span>
        <input value={name} disabled={laeuft} autoFocus onChange={(e) => setName(e.target.value)} />
      </label>

      {anDerWurzel && (
        <>
          <label className="suchzeile">
            <span className="name">{t('ledgeraufbau.neu.rechnungslegung')}</span>
            <input value={legung} disabled={laeuft} onChange={(e) => setLegung(e.target.value)} />
          </label>

          <p className="erlaeuterung">{t('ledgeraufbau.neu.rechnungslegung.erlaeuterung')}</p>

          {/*
            **Was es schon gibt, wird übernommen und nicht abgetippt.** Die
            Rechnungslegung wird zeichengenau verglichen — „hgb" neben „HGB"
            ergäbe eine Ebene, zu der der Abschluss kein Kapitel findet, und
            nichts bräche ab. Dasselbe Bauteil wie die aufnehmbaren Schichten
            weiter unten, aus demselben Grund.
          */}
          {legungen.length > 0 && (
            <p className="handreichung">
              {legungen.map((l) => (
                <button
                  key={l}
                  type="button"
                  className="schmal"
                  disabled={laeuft}
                  onClick={() => setLegung(l)}
                >
                  {l}
                </button>
              ))}
            </p>
          )}
        </>
      )}

      <p className="handreichung">
        <button
          type="button"
          className="haupt"
          disabled={laeuft || !vollstaendig}
          onClick={() => aufAnlegen(name.trim(), art, anDerWurzel ? legung.trim() : undefined)}
        >
          {t('stammdaten.neu.speichern')}
        </button>
        <button type="button" disabled={laeuft} onClick={aufAbbruch}>
          {t('import.schliessen')}
        </button>
      </p>

      {/*
        Was es schon gibt, wird AUFGENOMMEN und nicht neu angelegt: Eine zweite
        Rohdatenschicht neben derselben ersten wäre zweimal dieselbe Lieferung
        (R399). Deshalb steht die Liste im selben Blatt und nicht hinter einem
        zweiten Knopf — wer hier steht, hat die Wahl noch nicht getroffen.
      */}
      {aufAufnehmen && aufnehmbar && aufnehmbar.length > 0 && (
        <>

          <p className="handreichung">
            {aufnehmbar.map((k) => (
              <button
                key={k.schluessel}
                type="button"
                className="schmal"
                disabled={laeuft}
                onClick={() => aufAufnehmen(k.schluessel)}
              >
                {beschriftung(k.namen) || k.code}
              </button>
            ))}
          </p>
        </>
      )}
    </div>
  )
}
